import { getAdClickIds, getLandingPageUrl, getVisitorId } from '~/lib/helpers/adClickIds';
import { getJourneySummary } from '~/lib/helpers/userJourney';
import { getTrafficSource } from '~/lib/helpers/trafficSource';
import type { ConversionEventData, UserData } from './types';

/**
 * Every dataLayer event this app pushes. `fireConversionEvent` accepts only
 * these, so a typo cannot silently create an event no GTM tag listens for.
 *
 * Tag counts are live (non-paused) tags in container GTM-PTHJV3S, read from the
 * published container on 2026-09-23 — measured, not estimated.
 *
 * GTM also listens for two events this app never pushes, so those tags can
 * never fire: `pardot_form_submit` (14 tags) and `ServcorpBuyNow` (16 tags).
 * Noted here as dead weight to remove in GTM, not as values to push.
 */
export const DATA_LAYER_EVENTS = {
    /** Quote request submitted. Superseded by LEAD_SUBMITTED. */
    QUOTE_REQUEST: "quote_request",
    /** Tour booked. Superseded by LEAD_SUBMITTED. */
    BOOK_TOUR: "book_tour",
    ADD_LISTING_POPUP_REQUEST: "add_listing_popup_request",
    ADD_LISTING_SIGNUP_CLICK: "add_listing_signup_click",
    QUOTE_REQUEST_MODAL_CONTINUE: "quote_request_modal_continue",
    ADD_LISTING_MODAL_CONTINUE: "add_listing_modal_continue",
    CONVERSION_PAGE_VIEW: "conversion_page_view",
    LEAD_SUBMITTED: "lead_submitted",
    USER_JOURNEY: "user_journey",
    EMAIL_CAPTURED: "email_captured",
    PHONE_CLICK: "phone_click",
    WHATSAPP_CLICK: "whatsapp_click",
    ADD_LISTING_CLICK: "add_listing_click",
    /** A form became visible — `form` says which. */
    FORM_OPEN: "form_open",
    /** A step inside a multi-step form; carries `step` and `step_name`. */
    FORM_STEP: "form_step",
} as const;
export type DataLayerEvent = (typeof DATA_LAYER_EVENTS)[keyof typeof DATA_LAYER_EVENTS];

/**
 * RETIRED 2026-09-28 on Ali's instruction — kept commented out, not deleted.
 *
 * The names above were renamed to snake_case, and **62 live tags in
 * container GTM-PTHJV3S still listen for the old spellings** — 29 on `BookTour`,
 * 18 on `QuoteRequest`, 8 on `AddListingPopupRequest`, 3 on
 * `AddListingSignupClick` and 2 each on the two modal-continue events. Renaming
 * without this would have silenced all of them on deploy: every Google Ads
 * conversion, the Bing UET quote and tour events, Facebook, LinkedIn and the
 * GA4 quote/tour events, with nothing in the UI to show anything had broken.
 *
 * Until now `fireConversionEvent` pushed both spellings. It no longer does, so
 * those 62 tags stop firing until their triggers accept the snake_case names.
 *
 * To retire this: in GTM point those triggers at the snake_case names (or accept
 * both with a regex), publish, confirm in Tag Assistant, then delete this map
 * and the four lines in `fireConversionEvent` that use it.
 */
// const LEGACY_EVENT_NAMES: Partial<Record<DataLayerEvent, string>> = {
//     [DATA_LAYER_EVENTS.QUOTE_REQUEST]: "QuoteRequest",
//     [DATA_LAYER_EVENTS.BOOK_TOUR]: "BookTour",
//     [DATA_LAYER_EVENTS.ADD_LISTING_POPUP_REQUEST]: "AddListingPopupRequest",
//     [DATA_LAYER_EVENTS.ADD_LISTING_SIGNUP_CLICK]: "AddListingSignupClick",
//     [DATA_LAYER_EVENTS.QUOTE_REQUEST_MODAL_CONTINUE]: "quoteRequestModalContinue",
//     [DATA_LAYER_EVENTS.ADD_LISTING_MODAL_CONTINUE]: "addListingModalContinue",
// };

/**
 * Utility function to check if we're in a browser environment
 */
const isBrowser = (): boolean => {
    return typeof window !== "undefined";
};

/**
 * Safely push events to Google Tag Manager dataLayer
 */
const pushToDataLayer = (eventData: Record<string, any>): void => {
    if (!isBrowser()) {
        return;
    }

    try {
        if (window.dataLayer) {
            window.dataLayer.push(eventData);
        } else {
            console.warn("dataLayer not available for event:", eventData.event);
        }
    } catch (error) {
        console.error("Error pushing to dataLayer:", error);
    }
};

/**
 * Safely push events to Microsoft UET (Bing Ads)
 */
const pushToUET = (eventName: string, eventData?: Record<string, any>): void => {
    if (!isBrowser()) {
        return;
    }
    try {
        window.uetq = window.uetq || [];
        if (eventData) {
            window.uetq.push("event", eventName, eventData);
        } else {
            window.uetq.push("event", eventName);
        }
    } catch (error) {
        console.error("Error pushing to UET:", error);
    }
};

/**
 * Generic function to fire conversion events to all tracking services
 */
export const fireConversionEvent = (
    eventName: DataLayerEvent,
    eventData?: ConversionEventData
): void => {
    if (!eventName) {
        console.warn("Event name is required for conversion tracking");
        return;
    }

    // Prepare dataLayer event
    const dataLayerEvent = {
        event: eventName,
        ...eventData,
    };

    // Push to Google Tag Manager
    pushToDataLayer(dataLayerEvent);

    // Legacy spellings retired on Ali's instruction: only the snake_case names
    // are pushed now. Re-enable these four lines (and the map above) if the GTM
    // triggers listed there still need the old names.
    // const legacyName = LEGACY_EVENT_NAMES[eventName];
    // if (legacyName) {
    //     pushToDataLayer({ ...dataLayerEvent, event: legacyName });
    // }

    // Push to Microsoft UET
    const uetEventData = eventData?.user_data || eventData;
    pushToUET(eventName, uetEventData);

    // Log for debugging (only in development)
    if (process.env.NODE_ENV === 'development') {
        console.log('Conversion event fired:', eventName, eventData);
    }
};

/**
 * Track when user clicks on "Add Listing" signup
 */
export const handleAddListingConversion = (): void => {
    fireConversionEvent(DATA_LAYER_EVENTS.ADD_LISTING_SIGNUP_CLICK);
};

/**
 * Track when user submits add listing request with user data
 */
export const trackAddListingRequestConversion = (userData: UserData): void => {
    if (!userData.email && !userData.phone_number) {
        console.warn("User data should include email or phone_number for effective tracking");
    }

    fireConversionEvent(DATA_LAYER_EVENTS.ADD_LISTING_POPUP_REQUEST, {
        user_data: {
            email: userData.email,
            phone_number: userData.phone_number,
        },
    });
};

/**
 * Track quote request conversions
 */
export const trackQuoteRequestConversion = (userData?: UserData): void => {
    fireConversionEvent(DATA_LAYER_EVENTS.QUOTE_REQUEST, {
        user_data: userData,
    });
};

/** Lead sources. Wired today: quote_popup, quote_form, campaign_hero, book_tour,
 * and add_listing.
 * The rest are the agreed vocabulary for the sources still to be migrated
 * (contact and enquire_now are Pardot iframes, so the page cannot see them yet). */
export const LEAD_FORMS = {
    QUOTE_POPUP: "quote_popup",
    QUOTE_FORM: "quote_form",
    CAMPAIGN_HERO: "campaign_hero",
    BOOK_TOUR: "book_tour",
    CONTACT: "contact",
    ENQUIRE_NOW: "enquire_now",
    CHATBOT: "chatbot",
    ADD_LISTING: "add_listing",
} as const;
export type LeadForm = (typeof LEAD_FORMS)[keyof typeof LEAD_FORMS];

/** `partial` = the visitor gave contact details but did not finish the flow;
 * Pardot still creates a prospect, so the CRM counts it as a lead. */
export const LEAD_STATUS = {
    COMPLETE: "complete",
    PARTIAL: "partial",
} as const;
export type LeadStatus = (typeof LEAD_STATUS)[keyof typeof LEAD_STATUS];

export interface LeadEvent {
    form: LeadForm;
    status: LeadStatus;
    /** Salesforce/Pardot id when the backend returned one. */
    leadId?: string;
    userData?: UserData;
}

/** Region prefix of the current URL ("/uk/..." -> "uk"), else the default "us".
 * Lets one GTM tag per platform pick its account from a lookup table instead of
 * matching the page URL with a per-country condition. */
const currentRegionCode = (): string => {
    const [first] = window.location.pathname.split("/").filter(Boolean);
    return first && /^[a-z]{2}$/i.test(first) ? first.toLowerCase() : "us";
};

/** Everything both lead events carry: who the visitor is, where they came from
 * and how much they browsed. Kept in one place so the two can never drift. */
function attributionContext(userData?: UserData) {
    // Click IDs and the first landing URL survive internal navigation via the
    // consent-aware attribution storage in adClickIds.ts.
    const { msclkid, gclid, gbraid, wbraid } = getAdClickIds();

    return {
        country: currentRegionCode(),
        visitor_id: getVisitorId(),
        // Who sent them (bing, google, chatgpt) and how (paid, organic, llm).
        ...getTrafficSource(),
        // Six counters: how much browsing preceded this.
        ...getJourneySummary(),
        landing_page_url: getLandingPageUrl(),
        page_url: window.location.href,
        msclkid,
        gclid,
        gbraid,
        wbraid,
        msclkid_present: Boolean(msclkid),
        gclid_present: Boolean(gclid),
        user_data: userData,
    };
}

/**
 * The single lead event every platform tag should listen to.
 *
 * Call it from the submission workflow, never from a UI state such as a
 * confirmation heading becoming visible. Direct Salesforce flows call this
 * after a successful API response. Pardot's `no-cors` response is opaque, so
 * those flows can confirm dispatch but CRM field persistence still depends on
 * the form-handler mappings.
 *
 * Nothing is pushed to `uetq` here: Microsoft's tag is fired by GTM off this
 * event, which keeps one definition of "a lead" across every platform.
 */
export const trackLead = ({ form, status, leadId, userData }: LeadEvent): void => {
    if (!isBrowser()) {
        return;
    }

    pushToDataLayer({
        event: DATA_LAYER_EVENTS.LEAD_SUBMITTED,
        event_version: 1,
        form,
        status,
        lead_id: leadId || "",
        ...attributionContext(userData),
    });
};

/**
 * A verified email address was given, before any backend has the lead.
 *
 * Deliberately NOT `lead_submitted`: at this point nothing has reached Pardot or
 * Salesforce, so counting it as a lead would overstate the CRM. It exists to
 * measure drop-off after the most valuable step, and to give the ad platforms a
 * signal for visitors who then abandon the form.
 */
export const trackEmailCaptured = ({ form, email }: { form: LeadForm; email: string }): void => {
    if (!isBrowser() || !email) {
        return;
    }

    pushToDataLayer({
        event: DATA_LAYER_EVENTS.EMAIL_CAPTURED,
        event_version: 1,
        form,
        ...attributionContext({ email }),
    });
};

/** Step names per form, so a GA4 funnel reads without a decoder ring. A step
 * that is not listed still tracks, as `step_10` and so on — adding a step to a
 * form needs no change here, only a nicer label if one is wanted. */
const STEP_NAMES: Partial<Record<LeadForm, Record<number, string>>> = {
    [LEAD_FORMS.QUOTE_POPUP]: {
        1: "office_size",
        2: "duration",
        3: "budget",
        4: "move_in",
        5: "location",
        6: "email",
        7: "name_company",
        8: "comments_phone",
        9: "confirmation",
    },
    [LEAD_FORMS.CAMPAIGN_HERO]: {
        1: "location_desks_budget_email",
        2: "name_company_phone",
        3: "request_sent",
    },
};

/** The same form opening twice inside this window is one open. Desktop and
 * mobile surfaces of a form can both mount (one hidden by CSS), which would
 * otherwise double every Add Listing open — the same inflation the duplicated
 * GTM visibility triggers already cause. */
const FORM_OPEN_DEDUPE_MS = 1000;
const lastFormOpenAt: Record<string, number> = {};

/** A form became visible to the visitor: the top of every funnel. */
export const trackFormOpen = (form: LeadForm): void => {
    if (!isBrowser()) {
        return;
    }

    const now = Date.now();
    if (now - (lastFormOpenAt[form] ?? 0) < FORM_OPEN_DEDUPE_MS) {
        return;
    }
    lastFormOpenAt[form] = now;

    pushToDataLayer({
        event: DATA_LAYER_EVENTS.FORM_OPEN,
        event_version: 1,
        form,
        ...attributionContext(),
    });
};

/** A step inside a multi-step form. One event with a `step` parameter rather
 * than one event name per step: GA4 caps event names at 500 per property, its
 * funnel explorations are built from parameters anyway, and a tenth step then
 * needs no new code, no new tag and no new trigger. */
export const trackFormStep = ({ form, step }: { form: LeadForm; step: number }): void => {
    if (!isBrowser() || !Number.isFinite(step)) {
        return;
    }

    pushToDataLayer({
        event: DATA_LAYER_EVENTS.FORM_STEP,
        event_version: 1,
        form,
        step,
        step_name: STEP_NAMES[form]?.[step] ?? `step_${step}`,
        ...attributionContext(),
    });
};

/**
 * An engagement click: phone, WhatsApp, Add Listing.
 *
 * dataLayer only. These are intent signals for reporting, not conversions — do
 * not point an Ads conversion tag at them, or one visitor tapping a phone number
 * twice becomes two "leads", which is exactly the inflation the tour heading
 * already causes.
 */
export const trackEngagementClick = (
    eventName: DataLayerEvent,
    detail: { link_url: string; link_text: string },
): void => {
    if (!isBrowser()) {
        return;
    }

    pushToDataLayer({
        event: eventName,
        event_version: 1,
        ...detail,
        ...attributionContext(),
    });
};

/**
 * Generic function to fire custom events
 * @deprecated Use fireConversionEvent instead
 */
export const fireEvent = (eventName: string): void => {
    console.warn("fireEvent is deprecated, use fireConversionEvent instead");
    // Cast: this legacy entry point takes any string. It has no callers.
    fireConversionEvent(eventName as DataLayerEvent);
};

/**
 * Track form submissions with user data
 */
export const trackFormSubmission = (
    formType: string,
    userData?: UserData
): void => {
    // Cast: builds its name at runtime. No callers, and GTM has no tag for
    // any `<type>FormSubmission` event, so nothing it pushes is ever received.
    fireConversionEvent(`${formType}FormSubmission` as DataLayerEvent, {
        user_data: userData,
    });
};

/**
 * Track page views for specific conversion pages
 */
export const trackConversionPageView = (pageName: string): void => {
    fireConversionEvent(DATA_LAYER_EVENTS.CONVERSION_PAGE_VIEW, {
        page_name: pageName,
    });
};

/**
 * Initialize conversion tracking (call this once when app loads)
 */
export const initializeConversionTracking = (): void => {
    if (!isBrowser()) {
        return;
    }

    // Initialize dataLayer if it doesn't exist
    window.dataLayer = window.dataLayer || [];

    // Initialize UET queue if it doesn't exist
    window.uetq = window.uetq || [];

    // Log initialization in development
    if (process.env.NODE_ENV === 'development') {
        console.log('Conversion tracking initialized');
    }
};

// #unique-visitor-modal-heading
// #tour-confirmed-heading
